import {inject, injectable} from 'inversify';

import {i18n} from '@yourcoach/i18n';

import type {ProgramApiModel} from '../../../../api/program';
import type {IApiDataMapper} from '../../../ports/types';
import type {ProgramModel} from '../Program.model';

import {EditionMapperJrpc} from '../../Edition/mappers/Edition.mapper.jrpc';
import {FileMapperJrpc} from '../../File/mappers/File.mapper.jrpc';
import {ProgramFactory} from '../Program.factory';
import {ProgramExpandJrpc} from './Program.expand.jrpc';

@injectable()
export class ProgramMapperJrpc
  implements
    IApiDataMapper<ProgramMapperJrpc.ApiData, ProgramModel.Props, ProgramModel>
{
  constructor(
    @inject(ProgramFactory)
    private readonly factory: ProgramFactory,
    @inject(ProgramExpandJrpc)
    public readonly expand: ProgramExpandJrpc,
    @inject(FileMapperJrpc) private readonly fileMapper: FileMapperJrpc,
    @inject(EditionMapperJrpc)
    private readonly editionMapper: EditionMapperJrpc,
  ) {}

  mapDataToModel(apiData: ProgramMapperJrpc.ExpandedApiData): ProgramModel {
    const props = this.mapDataToProps(apiData);

    return this.factory.create(props);
  }

  mapDataToProps(
    apiData: ProgramMapperJrpc.ExpandedApiData,
  ): ProgramModel.Props {
    const id = apiData._id;
    const title = this.title(apiData);
    const avatar = this.avatar(apiData);
    const description = this.description(apiData);

    return {
      apiData,
      avatar,
      description,
      editions: this.editions(apiData),
      id,
      title,
    };
  }

  private avatar(props: ProgramMapperJrpc.ExpandedApiData) {
    if (props.avatar) {
      return this.fileMapper.mapDataToModel(props.avatar);
    }

    return null;
  }

  private description(props: ProgramMapperJrpc.ApiData) {
    return props.description ?? '';
  }

  private editions(props: ProgramMapperJrpc.ExpandedApiData) {
    return props.editions
      .filter(Boolean)
      .map(edition => this.editionMapper.mapDataToModel(edition));
  }

  private title(props: ProgramMapperJrpc.ApiData) {
    return props.title ?? i18n.t('shared:program_not_found');
  }
}

export namespace ProgramMapperJrpc {
  export type ApiData = ProgramApiModel;

  export type ExpandedApiData = ApiData & ProgramExpandJrpc.ExpandedFields;
}
